import React from 'react';

type ComparablePrimitive = Primitive | symbol | null | undefined;

type ComparableObject = | ComparablePrimitive[] | Set<ComparablePrimitive> | Map<ComparablePrimitive, ComparablePrimitive> | Record<string, ComparablePrimitive> | React.ReactElement | { current: ComparablePrimitive };

type Comparable = ComparablePrimitive | ComparableObject;

export function isUndefined(value: unknown): value is undefined {
  return value === undefined;
}

/**
 * Checks if a value is a plain JavaScript object
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function isRefLike(val: unknown): val is { current: unknown } {
  return typeof val === "object" && val !== null && Object.hasOwn(val, "current");
}

function isMap(val: unknown): val is Map<unknown, unknown> {
  return typeof val === "object" && val !== null && val instanceof Map;
}

function isSet(val: unknown): val is Set<unknown> {
  return typeof val === "object" && val !== null && val instanceof Set;
}

function isBothNull(a: unknown, b: unknown): boolean {
  return a === null && b === null;
}

function isBothValidReactElement(a: unknown, b: unknown): boolean {
  return React.isValidElement(a) && React.isValidElement(b);
}

function isBothRefLike(a: unknown, b: unknown): boolean {
  return isRefLike(a) && isRefLike(b);
}

function checkCondition(type: 'array' | 'map' | 'set', a: unknown, b: unknown): boolean {
  switch (type) {
    case 'array': {
      const arrA = a as unknown[];
      return (!Array.isArray(b) || arrA.length !== b.length);
    }
    case 'map': {
      const mapA = a as Map<unknown, unknown>;
      return (!isMap(b) || mapA.size !== b.size);
    }
    case 'set': {
      const setA = a as Set<unknown>;
      return (!isSet(b) || setA.size !== b.size);
    }
  }
}

const checkKeys = (key: string, objA: Record<string, Comparable>, objB: Record<string, Comparable>) => {
  return Object.hasOwn(objB, key) && isEqual(objA[key], objB[key]);
};

const checkIdentityOrNull = (a: unknown, b: unknown) => {
  return a === b || isBothNull(a, b);
}

const checkDifferentTypesOrFunctionComparison = (a: unknown, b: unknown) => {
  return typeof a !== typeof b || typeof a === 'function';
}

const checkReactElementOrRefLike = (a: unknown, b: unknown) => {
  return isBothValidReactElement(a, b) || isBothRefLike(a, b);
}

const checkMapB = (mapB: Map<Comparable, Comparable>, key: Comparable, val: Comparable) => {
  return !mapB.has(key) || !isEqual(val, mapB.get(key));
}

const getPlainObjectResult = (a: Record<string, Comparable>, b: Record<string, Comparable>) => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => checkKeys(key, a, b));
}

const getSetResult = (a: Set<Comparable>, b: unknown) => {
  if (checkCondition('set', a, b)) return false;
  const setB = b as Set<Comparable>;
  return [...a].every((val) => setB.has(val));
}

const getMapResult = (a: Map<Comparable, Comparable>, b: unknown) => {
  if (checkCondition('map', a, b)) return false;
  for (const [key, val] of a) {
    const mapB = b as Map<Comparable, Comparable>;
    if (checkMapB(mapB, key, val)) return false;
  }
  return true;
}

/**
 * Deep equality comparison between two values
 */
export function isEqual(a: Comparable, b: Comparable): boolean {
  // Identity comparison or null check
  if (checkIdentityOrNull(a, b)) return true;

  // Different types or  Function comparison (by reference)
  if (checkDifferentTypesOrFunctionComparison(a, b)) return false;

  // JSX Element / React Component or React Refs
  if (checkReactElementOrRefLike(a, b)) {
    return a === b;
  }

  // Arrays
  if (Array.isArray(a)) {
    if (checkCondition('array', a, b)) return false;
    const arrB = b as Array<Comparable>;
    return a.every((item, index) => isEqual(item, arrB[index]));
  }
  if (Array.isArray(b)) return false; // a is not an array but b is

  // Map
  if (isMap(a)) {
    return getMapResult(a, b);
  }
  if (isMap(b)) return false; // a is not a Map but b is

  // Set
  if (isSet(a)) {
    return getSetResult(a, b);
  }
  if (isSet(b)) return false; // a is not a Set but b is

  // Plain objects (includes style, className, dangerouslySetInnerHTML, etc.)
  if (isPlainObject(a as object) && isPlainObject(b as object)) {
    const objA = a as Record<string, Comparable>;
    const objB = b as Record<string, Comparable>;
    return getPlainObjectResult(objA, objB);
  }

  // Primitive (String, Number, Boolean, Symbol) & NaN
  return Object.is(a, b);
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: Parameters<T>) => void>(func: T, wait: number, immediate = false): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>): void {
    const later = (): void => {
      timeout = null;
      if (!immediate) {
        func.apply(this, args);
      }
    };

    const callNow = immediate && timeout === null;

    if (timeout !== null) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, wait);

    if (callNow) {
      func.apply(this, args);
    }
  };
}
